import { useMemo } from 'react'
import { useQuery, useParams } from '@truepill/tpos-react-router'
import { ProviderType, prescriberManagementRoles } from '@truepill/tpos-types'
import { ReactComponent as PrescriptionIcon } from 'assets/icons/prescriptionBottles.svg'
import { ReactComponent as PersonIcon } from 'assets/nav/person.svg'
import AuthLimited from 'components/AuthLimited'
import BreadCrumb from 'components/BreadCrumb'
import LoadingSpinner from 'components/Loading'
import NotesPane from 'components/NotesPane'
import { CancelButton, PageContainer, SaveButton, StickyPageHeadingContainer } from 'components/PageStructure'
import ToolBar, { EditButton, LogButton, NotesButton } from 'components/ToolBar'
import { GET_SYNCED_PRESCRIBER } from 'gql'
import useLogs from 'hooks/navigation/useLogs'
import usePrescriber from 'hooks/navigation/usePrescriber'
import useEditMode from 'hooks/useEditMode'
import useSetPageTitle from 'hooks/useSetPageTitle'
import { bodyPrimaryColor } from 'styles/styleVariables'
import type { Prescriber } from 'types'
import { getNotes } from 'utils'
import PrescriberAddresses from './components/PracticeAddresses'
import PrescriberPrescriptions from './components/PrescriberPrescriptions'
import PrescriberProfile from './components/PrescriberProfile'
import PrescriberTabs from './components/PrescriberTabs'
import {
  Title,
  LoadingSpinnerContainer,
  TitleSection,
  Container,
  WrapSection,
  Tab,
  PaddedActionButtonContainer,
} from './StyledComponents'

const isVeternarian = (prescriber: Prescriber) => {
  return prescriber.suffix === ProviderType.DVM
}

const PrescriberPage = (): JSX.Element => {
  const { prescriberId: id } = useParams<{ prescriberId: string }>()
  const {
    data,
    loading,
    error,
    refetch: refetchSyncedPrescriber,
  } = useQuery(GET_SYNCED_PRESCRIBER, {
    variables: { prescriberId: id, npi: id },
  })

  const [editMode, setEditMode] = useEditMode()

  const { logs, refetch: refetchLogs } = useLogs({
    prescriberId: id,
  })
  const notes = getNotes(logs || [])

  const prescriber = data?.getSyncedPrescriber as Prescriber

  const {
    savePrescriberChanges,
    loading: isSavingPrescriberChanges,
    resetPrescriberChanges,
  } = usePrescriber({ prescriber })

  const pageTitle = useMemo(() => {
    return !prescriber ? 'Prescriber' : `Prescriber - ${prescriber.firstName} ${prescriber.lastName}`
  }, [prescriber])

  useSetPageTitle(pageTitle)

  async function updatePrescriber() {
    try {
      await savePrescriberChanges()
      refetchLogs()
      refetchSyncedPrescriber()
      setEditMode(false)
    } catch (error) {
      console.error('Failed to update prescriber', error)
    }
  }

  if (error) {
    return (
      <PageContainer>
        <StickyPageHeadingContainer>
          <BreadCrumb />
        </StickyPageHeadingContainer>
        <Title>Error loading Prescriber: {JSON.stringify(error)}</Title>
      </PageContainer>
    )
  }

  if (loading || !prescriber) {
    return (
      <PageContainer>
        <StickyPageHeadingContainer />
        <LoadingSpinnerContainer>
          <LoadingSpinner />
        </LoadingSpinnerContainer>
      </PageContainer>
    )
  }

  return (
    <>
      <PageContainer>
        <BreadCrumb />
        <TitleSection>
          <Title>
            {prescriber.firstName} {prescriber.lastName}
            {prescriber.suffix ? `, ${prescriber.suffix}` : ''}
          </Title>

          {editMode && (
            <AuthLimited roles={prescriberManagementRoles}>
              <PaddedActionButtonContainer>
                <CancelButton
                  label="Cancel"
                  onClick={() => {
                    setEditMode(false)
                    resetPrescriberChanges()
                  }}
                />
                <SaveButton
                  label={`${isSavingPrescriberChanges ? 'Updating...' : 'Update'}`}
                  disabled={isSavingPrescriberChanges}
                  onClick={updatePrescriber}
                />
              </PaddedActionButtonContainer>
            </AuthLimited>
          )}
        </TitleSection>
        <PrescriberTabs>
          <Tab icon={<PersonIcon fill={bodyPrimaryColor} />} title="Profile">
            <Container>
              <WrapSection>
                <PrescriberProfile isVet={isVeternarian(prescriber)} prescriber={prescriber} notes={notes} />
                <PrescriberAddresses isVet={isVeternarian(prescriber)} prescriber={prescriber} />
              </WrapSection>
              <ToolBar>
                <AuthLimited roles={prescriberManagementRoles}>
                  <EditButton />
                </AuthLimited>
                <LogButton logs={logs} prescriberId={prescriber._id} />
                <NotesButton notesCount={notes.length} showBadge={notes.length > 0} />
              </ToolBar>
            </Container>
          </Tab>
          <Tab icon={<PrescriptionIcon fill={bodyPrimaryColor} />} title="Prescriptions">
            <Container>
              <PrescriberPrescriptions prescriberId={prescriber._id} />
              <ToolBar>
                <LogButton logs={logs} prescriberId={prescriber._id} />
                <NotesButton notesCount={notes.length} showBadge={notes.length > 0} />
              </ToolBar>
            </Container>
          </Tab>
        </PrescriberTabs>
      </PageContainer>
      <NotesPane prescriber={prescriber} logs={notes} showPrescriberNotes={true} />
    </>
  )
}

export default PrescriberPage
